import { useLazyQuery } from "@apollo/client";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { createEnumParam, StringParam, useQueryParams, withDefault } from "use-query-params";

import Tabs from "@components/buttons/Tabs";
import styles from "@components/carePlan/CarePlan.module.css";
import Goals from "@components/carePlan/Goals";
import EditModeBanner from "@components/EditModeBanner";
import HandleQuery from "@components/HandleQuery";
import ExplorePinsLoader from "@components/loaders/ExplorePinsLoader";
import SkeletonLoader from "@components/loaders/SkeletonLoader";
import Pins from "@components/Pins";
import { CarePlanSectionEnum, CarePlanTabEnum } from "@typing/Enums";
import {
  GetJourneyWithPlanQuery,
  GetPinsDocument,
  GetPinsQuery,
  GetPinsQueryVariables,
  PinResourceTypeEnum
} from "@typing/Generated";
import { Direction } from "@typing/Models";
import { fadeAnimation } from "@utils/animationUtils";
import { IconName } from "@utils/iconUtils";
import { isMobile } from "@utils/platformUtils";

type Props = {
  handleCurrentSectionChange: (section: CarePlanSectionEnum, direction: Direction, worksheetId?: string) => void;
  journey: GetJourneyWithPlanQuery["journey"];
};

const Plan = ({ handleCurrentSectionChange, journey }: Props) => {
  const libraryTabParam = createEnumParam<CarePlanTabEnum>(Object.values(CarePlanTabEnum));

  const [query, setQuery] = useQueryParams({
    goalId: withDefault({ ...StringParam, urlName: "carePlanGoalId" }, null),
    tab: withDefault({ ...libraryTabParam, urlName: "carePlanTab" }, CarePlanTabEnum.GOALS_INCOMPLETE)
  });

  const [tab, setTab] = useState<CarePlanTabEnum>(query.tab);

  useEffect(() => {
    if (query.tab !== tab) {
      setTab(query.tab);
    }
  }, [query.tab, tab]);

  const changeTab = (newTab: CarePlanTabEnum) => {
    setQuery({ tab: newTab });
    setTab(newTab);
  };

  const tabOptions = useMemo(() => {
    const values = Object.values(CarePlanTabEnum);

    return values.map(value => ({
      icon: value === CarePlanTabEnum.PINS ? ("thumbtack" as IconName) : undefined,
      textKey: `pages.carePlan.tabs.${value}`,
      value
    }));
  }, []);

  const animationVariants = fadeAnimation;

  const [getPins, getPinsQuery] = useLazyQuery<GetPinsQuery, GetPinsQueryVariables>(GetPinsDocument);

  useEffect(() => {
    if (tab === CarePlanTabEnum.PINS) {
      getPins({
        variables: {
          hideEditModePins: true,
          journeyId: journey.id,
          resourceTypes: [
            PinResourceTypeEnum.RESOURCE,
            PinResourceTypeEnum.WORKSHEET,
            PinResourceTypeEnum.ARTICLE,
            PinResourceTypeEnum.EMPLOYERBENEFIT
          ]
        }
      });
    }
  }, [getPins, journey.id, tab]);

  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div>
      <div className={styles.header}>
        {journey.hasEditModeGoals && journey.carePlanPublishedAt && (
          <div className="margin-20-bottom">
            <EditModeBanner multipleGoals />
          </div>
        )}
        <Tabs onChange={v => changeTab(v)} options={tabOptions} value={tab} />
      </div>

      <div
        className={
          tab === CarePlanTabEnum.PINS ? styles.mobileScrollOverflowWithTabNoBg : styles.mobileScrollOverflowWithTab
        }
        style={{ height: isMobile() ? `calc(${viewportHeight}px - 86px)` : undefined }}
      >
        <div className={styles.relativeContainer}>
          <AnimatePresence>
            {(() => {
              switch (tab) {
                case CarePlanTabEnum.GOALS_COMPLETE:
                  return (
                    <motion.div
                      animate="animate"
                      exit="exit"
                      initial="initial"
                      key={CarePlanTabEnum.GOALS_COMPLETE}
                      style={{ position: "absolute", width: "100%" }}
                      variants={animationVariants}
                    >
                      <Goals
                        completed
                        goalId={query.goalId}
                        handleCurrentSectionChange={handleCurrentSectionChange}
                        journey={journey}
                      />
                    </motion.div>
                  );
                case CarePlanTabEnum.GOALS_INCOMPLETE:
                  return (
                    <motion.div
                      animate="animate"
                      exit="exit"
                      initial="initial"
                      key={CarePlanTabEnum.GOALS_INCOMPLETE}
                      style={{ position: "absolute", width: "100%" }}
                      variants={animationVariants}
                    >
                      <Goals
                        completed={false}
                        goalId={query.goalId}
                        handleCurrentSectionChange={handleCurrentSectionChange}
                        journey={journey}
                      />
                    </motion.div>
                  );
                case CarePlanTabEnum.PINS:
                  return (
                    <motion.div
                      animate="animate"
                      exit="exit"
                      initial="initial"
                      key={CarePlanTabEnum.PINS}
                      style={{ position: "absolute", width: "100%" }}
                      variants={animationVariants}
                    >
                      <SkeletonLoader loadingContent={<ExplorePinsLoader />} queries={[getPinsQuery]}>
                        <HandleQuery query={getPinsQuery}>
                          {pinsData => <Pins journeyId={journey.id} pins={pinsData.pins} />}
                        </HandleQuery>
                      </SkeletonLoader>
                    </motion.div>
                  );
              }
            })()}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Plan;
